import { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { snapTarget, wrappedSnapTarget, lerp } from './carouselMath';

/**
 * 스냅 시간 상수(초). 남은 거리가 이 시간마다 1/e 로 줄어든다 — Mobbin 스냅 피커 실측(놓은 뒤
 * 83ms 51% · 167ms 19% · 약 430ms 정지, 튕김 없음)에 맞춘 값. 프레임 수가 아니라 경과 시간으로
 * 줄이므로 60Hz·120Hz 에서 같은 시간에 같은 자리에 있다.
 */
const SNAP_TAU = 0.11;
/** 이 이상이면 마우스 휠 한 번 딸깍으로 보고 한 칸 넘긴다. 그보다 작은 트랙패드 delta 는 연속으로 따라간다. */
const WHEEL_NOTCH_DELTA = 50;
/** 섹션 간격을 모를 때(측정 전) 드래그 px→칸 환산에 쓰는 값 */
const FALLBACK_DRAG_STEP_PX = 250;

interface CarouselPhysicsOptions {
  /** Total number of items in the carousel */
  itemCount: number;
  /** Wheel sensitivity multiplier (트랙패드 연속 스크롤) */
  wheelSensitivity?: number;
  /** 섹션 한 칸의 중심 간 거리(px). 드래그가 손가락을 1:1 로 따라가게 환산한다 */
  getDragStepPx?: () => number;
  /** Whether to disable animations (reduced motion) */
  reducedMotion?: boolean;
  /** Callback when user clicks an item */
  onItemClick?: (index: number) => void;
}

interface CarouselPhysicsResult {
  offset: number;
  isDragging: boolean;
  trackRef: React.RefObject<HTMLDivElement | null>;
  handlePointerDown: (e: React.PointerEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  snapTo: (index: number) => void;
}

export function useCarouselPhysics({
  itemCount,
  wheelSensitivity = 0.008,
  getDragStepPx,
  reducedMotion = false,
  onItemClick,
}: CarouselPhysicsOptions): CarouselPhysicsResult {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);

  // Mutable refs for animation loop state
  const stateRef = useRef({
    offset: 0,
    isSnapping: false,
    snapTargetVal: 0,
    lastTime: 0,
    wheelTimer: null as ReturnType<typeof setTimeout> | null,
    // Drag tracking
    dragStartY: 0,
    dragStartOffset: 0,
    dragStepPx: FALLBACK_DRAG_STEP_PX,
    pointerHistory: [] as Array<{ y: number; time: number }>,
    // Click-snap time-based animation
    clickSnap: false,
    clickSnapStart: 0,
    clickSnapFrom: 0,
    clickSnapDuration: 0,
  });

  // Stable ref for options that change
  const optionsRef = useRef({ wheelSensitivity, getDragStepPx });
  useLayoutEffect(() => {
    optionsRef.current = { wheelSensitivity, getDragStepPx };
  });

  // --- rAF animation loop ---
  const rafId = useRef(0);
  const tickRef = useRef<FrameRequestCallback | null>(null);

  const tick = useCallback(
    (now: number) => {
      const s = stateRef.current;
      if (itemCount === 0) {
        setOffset(0);
        if (tickRef.current) rafId.current = requestAnimationFrame(tickRef.current);
        return;
      }

      const dt = s.lastTime ? Math.min((now - s.lastTime) / 1000, 0.1) : 0.016;
      s.lastTime = now;

      if (s.clickSnap) {
        const elapsed = now - s.clickSnapStart;
        const progress = Math.min(elapsed / s.clickSnapDuration, 1);
        const eased = 1 - (1 - progress) ** 3;
        s.offset = s.clickSnapFrom + (s.snapTargetVal - s.clickSnapFrom) * eased;
        if (progress >= 1) {
          s.offset = s.snapTargetVal;
          s.clickSnap = false;
          s.isSnapping = false;
        }
      } else if (s.isSnapping) {
        const alpha = reducedMotion ? 1 : 1 - Math.exp(-dt / SNAP_TAU);
        s.offset = lerp(s.offset, s.snapTargetVal, alpha);
        if (Math.abs(s.offset - s.snapTargetVal) < 0.005) {
          s.offset = s.snapTargetVal;
          s.isSnapping = false;
        }
      }

      setOffset(s.offset);

      // Only continue rAF if there's active animation — otherwise idle
      const isAnimating = s.isSnapping || s.clickSnap;
      if (isAnimating && tickRef.current) {
        rafId.current = requestAnimationFrame(tickRef.current);
      } else {
        rafId.current = 0; // idle — will restart on next interaction
      }
    },
    [itemCount, reducedMotion]
  );
  useLayoutEffect(() => {
    tickRef.current = tick;
  });

  const startLoop = useCallback(() => {
    if (rafId.current === 0 && tickRef.current) {
      stateRef.current.lastTime = 0;
      rafId.current = requestAnimationFrame(tickRef.current);
    }
  }, []);

  useEffect(() => {
    cancelAnimationFrame(rafId.current);
    stateRef.current.lastTime = 0;
    rafId.current = requestAnimationFrame(tick);
    const state = stateRef.current;
    return () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
      if (state.wheelTimer) clearTimeout(state.wheelTimer);
    };
  }, [tick]);

  // --- P0 Fix: Native wheel listener with { passive: false } ---
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const s = stateRef.current;
      const opts = optionsRef.current;
      startLoop();

      // 마우스 휠 한 번 딸깍(또는 줄·페이지 단위 delta)은 한 칸. 이어서 굴리면 목표에서 한 칸씩 더 간다
      if (e.deltaMode !== 0 || Math.abs(e.deltaY) >= WHEEL_NOTCH_DELTA) {
        if (s.wheelTimer) clearTimeout(s.wheelTimer);
        // 아직 한 칸 넘게 남았으면 더 쌓지 않는다 — 트랙패드로 세게 쓸면 큰 delta 가 몰려와 링을 몇 바퀴씩 돈다
        if (s.isSnapping && Math.abs(s.snapTargetVal - s.offset) > 1) return;
        const base = s.isSnapping ? s.snapTargetVal : snapTarget(s.offset);
        s.clickSnap = false;
        s.isSnapping = true;
        s.snapTargetVal = base + Math.sign(e.deltaY);
        return;
      }

      s.isSnapping = false;
      s.offset += e.deltaY * opts.wheelSensitivity;
      setOffset(s.offset);

      // Debounce snap after wheel stops
      if (s.wheelTimer) clearTimeout(s.wheelTimer);
      s.wheelTimer = setTimeout(() => {
        s.isSnapping = true;
        s.snapTargetVal = snapTarget(s.offset);
      }, 150);
    };

    track.addEventListener('wheel', handler, { passive: false });
    return () => track.removeEventListener('wheel', handler);
  }, [startLoop]);

  // --- P1 Fix: Normalize offset when itemCount changes ---
  useEffect(() => {
    const s = stateRef.current;
    if (itemCount > 0 && s.offset >= itemCount) {
      s.offset = 0;
      s.isSnapping = true;
      s.snapTargetVal = 0;
    }
  }, [itemCount]);

  // --- Pointer (drag) handlers ---
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const s = stateRef.current;
      s.isSnapping = false;
      s.clickSnap = false;
      s.dragStartY = e.clientY;
      s.dragStepPx = optionsRef.current.getDragStepPx?.() || FALLBACK_DRAG_STEP_PX;
      s.dragStartOffset = s.offset;
      s.pointerHistory = [{ y: e.clientY, time: e.timeStamp }];

      setIsDragging(true);

      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent) => {
        const dy = ev.clientY - s.dragStartY;
        // Invert: dragging down moves offset forward (items scroll up). 한 칸 간격(px)으로 나눠 손가락과 1:1
        s.offset = s.dragStartOffset - dy / s.dragStepPx;
        setOffset(s.offset);

        // Track last 3 positions for velocity
        s.pointerHistory.push({ y: ev.clientY, time: ev.timeStamp });
        if (s.pointerHistory.length > 3) s.pointerHistory.shift();
      };

      const onUp = (ev: PointerEvent) => {
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
        target.releasePointerCapture(ev.pointerId);
        setIsDragging(false);

        // 손을 떼는 순간 도착 칸을 정한다: 지금 속도로 τ 동안 갈 거리를 더해 반올림.
        // 같은 τ 로 지수 수렴하면 출발 속도가 손의 속도와 이어지고, 지나쳤다가 되돌아오지 않는다
        let velocity = 0; // 칸/초
        const history = s.pointerHistory;
        if (history.length >= 2) {
          const first = history[0] as (typeof history)[number];
          const last = history[history.length - 1] as (typeof history)[number];
          const dtMs = last.time - first.time;
          if (dtMs > 0) velocity = (-(last.y - first.y) / dtMs / s.dragStepPx) * 1000;
        }
        s.isSnapping = true;
        s.snapTargetVal = snapTarget(s.offset + velocity * SNAP_TAU);
        startLoop();
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    },
    [startLoop]
  );

  // --- Keyboard handler ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const s = stateRef.current;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        startLoop();
        const direction = e.key === 'ArrowUp' ? -1 : 1;
        const currentSnap = snapTarget(s.offset);
        const nextIndex = (((currentSnap + direction) % itemCount) + itemCount) % itemCount;
        s.isSnapping = true;
        s.snapTargetVal = wrappedSnapTarget(s.offset, nextIndex, itemCount);
      }
    },
    [itemCount, startLoop]
  );

  // --- Snap to specific index (shortest path around the ring) ---
  const snapToIndex = useCallback(
    (index: number) => {
      const s = stateRef.current;
      startLoop();

      const target = wrappedSnapTarget(s.offset, index, itemCount);
      const distance = Math.abs(target - s.offset);

      if (reducedMotion || distance < 0.01) {
        s.offset = target;
        s.isSnapping = false;
        s.clickSnap = false;
      } else {
        s.clickSnap = true;
        s.isSnapping = true;
        s.clickSnapStart = performance.now();
        s.clickSnapFrom = s.offset;
        s.clickSnapDuration = 250 + Math.min(distance, 3) * 50;
      }
      s.snapTargetVal = target;
      onItemClick?.(index);
    },
    [onItemClick, itemCount, reducedMotion, startLoop]
  );

  return {
    offset,
    isDragging,
    trackRef,
    handlePointerDown,
    handleKeyDown,
    snapTo: snapToIndex,
  };
}
